import { type FC, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";

import { AddressPicker } from "@/shared/components/ui/AddressPicker";
import { useCountyFromZip } from "@/shared/hooks/useCountyFromZip";
import { supabase } from "@/shared/lib/supabase";
import {
  EMPTY_ADDRESS,
  buildAddress,
  stripCountySuffix,
  type AddressParts,
} from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { sentryUtils } from "@/shared/utils/sentry";

import type { CanonicalAddress } from "@/shared/hooks/useCanonicalAddress";
import type { EstateSize } from "@/shared/utils/estateSize";

import { SummaryCard, SummaryRow } from "./SummaryCard";
import { getStateName } from "../summaryFormat";

import type { SectionEditProps } from "./sectionEdit";

const toParts = (address: CanonicalAddress | null | undefined): AddressParts =>
  address
    ? {
        streetAddress: address.street1,
        apartment: address.street2 ?? "",
        city: address.city ?? "",
        state: address.state ?? "",
        zipCode: address.zip,
      }
    : EMPTY_ADDRESS;

const titleCase = (value: string) => value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const countyName = (county: string | null | undefined) => (county ? stripCountySuffix(county) : "");

async function openDomicileAddressId(estateId: number): Promise<number | null> {
  const { data } = await supabase
    .from("entity_addresses")
    .select("address_id")
    .eq("estate_id", estateId)
    .eq("purpose", "domicile")
    .is("valid_to", null)
    .limit(1)
    .maybeSingle();
  return data?.address_id ?? null;
}

function useCountyCourtName(state: string | null | undefined, county: string | null | undefined) {
  return useQuery({
    queryKey: ["countyCourt", state, county],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courts")
        .select("name")
        .eq("state", state!)
        .eq("county", county!.toUpperCase())
        .order("id")
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.name ?? null;
    },
    enabled: !!state && !!county,
  });
}

interface EstateLocationSectionProps extends SectionEditProps {
  estateId: number;
  domicile: CanonicalAddress | null | undefined;
  caseCourtName: string | null;
  propertyStates: string[];
  estateSize: EstateSize;
}

export const EstateLocationSection: FC<EstateLocationSectionProps> = ({
  estateId,
  domicile,
  caseCourtName,
  propertyStates,
  estateSize,
  isEditing,
  canEdit,
  onEdit,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [address, setAddress] = useState<AddressParts>(EMPTY_ADDRESS);
  const [isSaving, setIsSaving] = useState(false);
  const { data: countyFromZip } = useCountyFromZip(domicile?.county ? null : domicile?.zip);
  const county = countyName(domicile?.county || countyFromZip);
  const { data: countyCourtName } = useCountyCourtName(domicile?.state, county);
  const countyLabel = county ? `${titleCase(county)} County` : "";
  const states = [...new Set([domicile?.state, ...propertyStates].filter(Boolean))]
    .map(getStateName)
    .join(", ");
  const { propertyCount } = estateSize;

  const handleEdit = () => {
    setAddress(toParts(domicile));
    onEdit();
  };

  const handleDone = async () => {
    const raw = buildAddress(address).trim();
    if (!raw) {
      onClose();
      return;
    }
    setIsSaving(true);
    try {
      const entity = { estate_id: estateId };
      const previousAddressId = await openDomicileAddressId(estateId);
      const result = await resolveAddressLink({ raw, purpose: "domicile", entity });
      const isAccepted = result?.status === "valid" || result?.status === "partial";
      if (result?.addressId && !isAccepted && previousAddressId) {
        await resolveAddressLink({ addressId: previousAddressId, purpose: "domicile", entity });
      }
      await queryClient.invalidateQueries({ queryKey: ["canonicalAddress"] });

      if (!result?.addressId) {
        enqueueSnackbar("Couldn't save the address. Please try again.", { variant: "error" });
        return;
      }
      if (!isAccepted) {
        enqueueSnackbar("We couldn't verify this address. Check it and try again.", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(TOAST.CHANGES_SAVED, { variant: "success" });
      onClose();
    } catch (err) {
      enqueueSnackbar("Couldn't save the address. Please try again.", { variant: "error" });
      sentryUtils.captureError(err, "Summary domicile save failed", { estateId });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SummaryCard
      title="Estate size and location"
      edit={{
        isEditing,
        canEdit,
        isSaving,
        onEdit: handleEdit,
        onCancel: onClose,
        onDone: handleDone,
      }}
    >
      <SummaryRow label="Home at passing">
        {isEditing ? (
          <AddressPicker
            value={address}
            onChange={setAddress}
            labels={{ streetAddress: "Street address", apartment: "Apartment, suite" }}
            stateFormat="abbreviation"
          />
        ) : (
          domicile && buildAddress(toParts(domicile))
        )}
      </SummaryRow>
      <SummaryRow label="County and court">
        {caseCourtName || countyCourtName || countyLabel}
      </SummaryRow>
      <SummaryRow label="States involved">{states}</SummaryRow>
      <SummaryRow label="Real property">
        {propertyCount > 0 && `${propertyCount} ${propertyCount === 1 ? "property" : "properties"}`}
      </SummaryRow>
      <SummaryRow label="Personal property">{estateSize.personalProperty?.long}</SummaryRow>
      <SummaryRow label="Estimated gross estate">{estateSize.gross?.long}</SummaryRow>
    </SummaryCard>
  );
};
